// File: ModelSplash - "Select model" picker shown before the configurator
// Created: 2026-09-25
// Updated: 2026-09-25
// Author: Erick Hernández Silva

import React from 'react';
import type { ShowroomItem } from './carWizardTypes';
import { isRenderableImage } from './carModel';
import { loadBlatant, BLATANT_ENABLED, BLATANT_FAMILY } from '../../assets/fonts/loadBlatant';

loadBlatant();

const LOGO_URL = 'https://cdn.prod.website-files.com/68472be536d16d9004a489e5/6848a4a992a3670f3d9c446a_Logo%20touring.png';

interface Props {
  cars: ShowroomItem[];
  onPick: (car: ShowroomItem) => void;
  embedded?: boolean;
}

// The card shows the model (e.g. "Supercabrio"); the car name is the fallback.
const cardTitle = (car: ShowroomItem) => car.modelName?.trim() || car.carName;

const ModelSplash: React.FC<Props> = ({ cars, onPick, embedded = false }) => (
  <div className={`bg-white text-black ${embedded ? 'min-h-[700px]' : 'min-h-screen'} flex flex-col`}>
    <header className="flex items-center justify-between px-4 pt-6 md:px-8 md:pt-8">
      <h1 className="text-2xl md:text-4xl font-normal tracking-tight">Select model</h1>
      <a href="https://www.touringdesignco.com/" rel="noopener noreferrer" target="_self" className="shrink-0">
        <img src={LOGO_URL} alt="Touring" className="h-6 md:h-7 w-auto invert" />
      </a>
    </header>

    <main className="flex-1 flex items-center justify-center px-4 py-8 md:px-8 md:py-10">
      {/* Cards are capped by viewport height so the whole picker fits on one screen. */}
      <div className={`grid w-full max-w-6xl gap-10 md:gap-16 justify-items-center ${cars.length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : cars.length > 1 ? 'md:grid-cols-2' : ''}`}>
        {cars.map((car) => (
          <button
            key={`${car.id}|${car.createdAt}`}
            type="button"
            onClick={() => onPick(car)}
            className="group flex flex-col items-center text-center focus:outline-none w-full max-w-[min(100%,58vh)]"
          >
            <div className="relative w-full aspect-square overflow-hidden bg-gray-100">
              {isRenderableImage(car.splash?.image) ? (
                <img
                  src={car.splash!.image}
                  alt={cardTitle(car)}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No image</div>
              )}
            </div>
            <div
              className="mt-5 md:mt-6 text-2xl md:text-3xl font-bold uppercase tracking-wide group-hover:underline group-focus-visible:underline underline-offset-8"
              style={BLATANT_ENABLED ? { fontFamily: BLATANT_FAMILY } : undefined}
            >
              {cardTitle(car)}
            </div>
            {car.splash?.description && (
              // pre-line: line breaks typed in the admin (e.g. "Convertible * V8") are kept.
              <p className="mt-3 max-w-md text-sm md:text-base leading-snug text-gray-900 whitespace-pre-line">
                {car.splash.description}
              </p>
            )}
          </button>
        ))}
      </div>
    </main>
  </div>
);

export default ModelSplash;
